// Keeps an open or installed app (iOS home-screen PWA) on the latest deploy.
//
// iOS does not reload a home-screen app when it is reopened: it resumes the page
// that is still in memory, sometimes for days, so it keeps running the old bundle
// no matter how the files are cached. Each deploy writes /version.json with the
// build id that is also baked into the bundle (REACT_APP_BUILD_ID); when they
// differ, the app reloads itself at a moment that can't interrupt the user.

const BUILD_ID = process.env.REACT_APP_BUILD_ID;
const VERSION_URL = '/version.json';
// Don't check more often than this when the app is resumed or polled.
const MIN_CHECK_INTERVAL_MS = 5 * 60 * 1000;
// While the app stays open and visible, check this often.
const POLL_INTERVAL_MS = 30 * 60 * 1000;

let lastCheck = 0;
let updateReady = false;

const fetchLatestBuildId = async (): Promise<string | null> => {
  try {
    const res = await fetch(`${VERSION_URL}?t=${Date.now()}`, { cache: 'no-store' });
    if (!res.ok) return null;
    const data = await res.json();
    return typeof data?.buildId === 'string' ? data.buildId : null;
  } catch {
    return null; // offline or not deployed with a version file: try again later
  }
};

// A reload would drop whatever the user is typing.
const isUserEditing = () => {
  const el = document.activeElement as HTMLElement | null;
  if (!el) return false;
  return el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || el.isContentEditable;
};

const checkForUpdate = async (force = false) => {
  if (updateReady) return;
  const now = Date.now();
  if (!force && now - lastCheck < MIN_CHECK_INTERVAL_MS) return;
  lastCheck = now;
  const latest = await fetchLatestBuildId();
  if (latest && latest !== BUILD_ID) updateReady = true;
};

export const startAppUpdateWatcher = () => {
  // Local dev and builds made outside deploy-webapp.sh have no build id.
  if (!BUILD_ID) return;

  // Back in the foreground: the user hasn't started anything yet, so reload
  // right away unless they left in the middle of typing.
  document.addEventListener('visibilitychange', async () => {
    if (document.visibilityState !== 'visible') return;
    await checkForUpdate();
    if (updateReady && !isUserEditing()) window.location.reload();
  });

  // Found while the app is in use: reload on the next in-app navigation, which
  // replaces the screen anyway. The URL has already changed by then, so the
  // reload opens the page the user asked for.
  const reloadIfReady = () => {
    if (updateReady) window.location.reload();
  };
  window.addEventListener('urlchange', reloadIfReady);
  window.addEventListener('popstate', reloadIfReady);

  window.setInterval(() => {
    if (document.visibilityState === 'visible') checkForUpdate();
  }, POLL_INTERVAL_MS);

  // Covers a page restored from the back/forward cache.
  window.addEventListener('pageshow', (e) => {
    if (e.persisted) checkForUpdate(true).then(() => updateReady && window.location.reload());
  });
};
